import { useMemo } from 'react';
import { ProjectId } from 'api/models';
import { PhaseEntityType, converToEntityId, extractParts } from 'data/projects/phase-entity';
import { useTasksFromStorePreferLightRQ } from 'data/tasks/hooks/use-tasks-from-store';
import { useProjectPhaseByProjectId } from './use-project-phase.store';
import { useSprints } from './use-sprints';
const frameTaskObject = (task, rootIndex)=>{
    var _task_dependentTasks;
    return {
        key: `TASK-${task.taskId}`,
        id: task.taskId,
        entityId: converToEntityId(task.taskId, PhaseEntityType.Task),
        title: task.taskName,
        startDate: task.startDate,
        dueDate: task.dueDate,
        assignee: task.assignee,
        private: task.private,
        dependentTasks: (_task_dependentTasks = task.dependentTasks) !== null && _task_dependentTasks !== void 0 ? _task_dependentTasks : [],
        data: task,
        rootIndex
    };
};
/**
 * `phaseId` comes from the phase whose entityOrder holds the token, not from
 * `sprint.projectPhase` — the token's position is what the reorder writes back.
 */ const frameSprintObject = (sprint, rootIndex, phaseId)=>({
        key: `SPRINT-${sprint.sprintId}`,
        id: sprint.sprintId,
        entityId: converToEntityId(sprint.sprintId, PhaseEntityType.Sprint),
        title: sprint.sprintName,
        startDate: sprint.startDate,
        dueDate: sprint.dueDate,
        private: sprint.private,
        phaseId,
        level: 1,
        type: 'SPRINT',
        children: [],
        data: sprint,
        rootIndex
    });
const populateChildren = (task, index, level, allTasks)=>{
    if (!task) {
        return [];
    } // Return an empty array if task is undefined
    const children = [];
    // Check if the task has subtasks
    if (task.subTaskOrder && task.subTaskOrder.length > 0) {
        children.push(...task.subTaskOrder.reduce((acc, subtaskId)=>{
            const subTask = allTasks.find((_task)=>_task.taskId === subtaskId);
            if (subTask) {
                var _task_subTaskOrder;
                acc.push({
                    ...frameTaskObject(subTask, index),
                    type: 'TASK',
                    level: level + 1,
                    parentTotal: task === null || task === void 0 ? void 0 : (_task_subTaskOrder = task.subTaskOrder) === null || _task_subTaskOrder === void 0 ? void 0 : _task_subTaskOrder.length,
                    children: populateChildren(subTask, index, level + 1, allTasks)
                });
            }
            return acc;
        }, []));
    }
    return children;
};
export const useTransformTabularData = (projectId)=>{
    const projectPhases = useProjectPhaseByProjectId(projectId[1]);
    const allTasks = useTasksFromStorePreferLightRQ(ProjectId(projectId[1]));
    const { data: sprintsData } = useSprints(ProjectId(projectId[1]));
    const sprints = useMemo(()=>sprintsData !== null && sprintsData !== void 0 ? sprintsData : [], [
        sprintsData
    ]);
    const tabData = useMemo(()=>{
        return projectPhases.map((projectPhase, index)=>{
            var _projectPhase_dependentProjectPhases, _projectPhase_entityOrder;
            return {
                key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
                id: projectPhase.projectPhaseId,
                title: projectPhase.projectPhaseName,
                startDate: projectPhase.startDate,
                dueDate: projectPhase.dueDate,
                type: 'PROJECT_PHASE',
                private: projectPhase.private,
                level: 0,
                data: projectPhase,
                rootIndex: index,
                parentTotal: 0,
                dependentProjectPhases: (_projectPhase_dependentProjectPhases = projectPhase.dependentProjectPhases) !== null && _projectPhase_dependentProjectPhases !== void 0 ? _projectPhase_dependentProjectPhases : [],
                entityOrder: (_projectPhase_entityOrder = projectPhase.entityOrder) !== null && _projectPhase_entityOrder !== void 0 ? _projectPhase_entityOrder : [],
                children: (projectPhase === null || projectPhase === void 0 ? void 0 : projectPhase.entityOrder) === undefined ? [] : projectPhase.entityOrder.reduce((accumulator, id)=>{
                    const { entityType, id: entityId } = extractParts(id);
                    // Sprints render as siblings of top-level tasks: the phase's
                    // entityOrder is one mixed list and both kinds live in it.
                    if (entityType === PhaseEntityType.Sprint) {
                        const sprint = sprints.find((_sprint)=>_sprint.sprintId === entityId);
                        if (sprint) {
                            accumulator.push(frameSprintObject(sprint, index, projectPhase.projectPhaseId));
                        }
                        return accumulator;
                    }
                    const task = allTasks.find((_task)=>converToEntityId(_task.taskId, PhaseEntityType.Task) === id);
                    if (task) {
                        var _projectPhase_entityOrder;
                        accumulator.push({
                            ...frameTaskObject(task, index),
                            type: 'TASK',
                            level: 1,
                            // Authoritative phase for drop targeting — the phase whose
                            // entityOrder holds the token, not the task's own field.
                            phaseId: projectPhase.projectPhaseId,
                            parentTotal: projectPhase === null || projectPhase === void 0 ? void 0 : (_projectPhase_entityOrder = projectPhase.entityOrder) === null || _projectPhase_entityOrder === void 0 ? void 0 : _projectPhase_entityOrder.length,
                            children: populateChildren(task, index, 1, allTasks)
                        });
                    }
                    return accumulator;
                }, [])
            };
        });
    }, [
        projectPhases,
        allTasks,
        sprints
    ]);
    return tabData;
};
